import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SurreyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Surrey Pothole Claims | 273,383 Potholes Filled in Five Years | Fixtyer"
        description="Surrey County Council filled 273,383 potholes in five years — a record 59,924 in 2024/25 alone — while spending roughly triple its DfT allocation. Two-thirds of the network is surveyed only once every four years. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/surrey"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Surrey', url: '/councils/surrey' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Surrey spends roughly three times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN — Surrey spent £100.5m of capital against a £28.6m allocation in 2024/25 — but the ratings that matter for your claim are Condition and Best Practice, and both are AMBER. Section 58 of the Highways Act 1980 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate. A council filling nearly 60,000 potholes a year is, by its own numbers, running a network that produces defects faster than its spending prevents them.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Surrey?',
            answer: "Unclassified roads make up 3,253km — 67% of Surrey's 4,844km network — and the council's own report states they are \"traditionally surveyed once every 4 years.\" In 2024, 22% of U-roads were in RED condition, the worst figure in the five years published. A four-year gap between condition surveys on two-thirds of the network is a structural weakness in any Section 58 defence: the council must show it had reasonable knowledge of the road's condition, and a road last surveyed up to four years ago is hard to know much about.",
          },
          {
            question: 'Surrey says the rise in RED unclassified roads is mostly a survey methodology change — does that hurt my claim?',
            answer: 'Not really. Surrey switched to a video and machine-learning survey in 2022 and says the new technology adds about 5 percentage points to the RED score, so "the increase in red condition shown in 2023 and 2024 is primarily due to the change in survey methodology rather than a significant deterioration in condition." But RED went from 16% in 2020 to 22% in 2024 — a 6-point rise — so even on the council\'s own adjustment the network has not improved. And whatever the cause of the measurement, 22% RED means roughly 715km of Surrey\'s residential roads should currently be considered for maintenance.',
          },
          {
            question: 'Pothole repairs hit a record 59,924 in 2024/25 — what does that mean for my claim?',
            answer: 'It means Surrey\'s network produces potholes at an extraordinary rate — about 164 filled every day in 2024/25, and 273,383 over five years. The council frames the rising trend as proactivity: repair gangs now "fix other potholes they identify in the vicinity of a pothole that has been reported." For a claimant, the volume cuts the other way: a network generating this many defects is one where potholes routinely form, and persist, between inspections — exactly the scenario where prior reports and dated photographs decide claims.',
          },
          {
            question: 'Surrey is prioritising A and B roads in 2025/26 — does that matter for my claim?',
            answer: 'It can. The council\'s own plan says it will "focus on improving the most used roads, our A and B network, with a higher proportion of budget allocation compared to the lesser used roads." A and B roads together are about a third of the network; the C and unclassified roads receiving a smaller share are where condition is weakest — 22% of U-roads are RED. If your pothole was on a deprioritised road, the question for the council is how a reduced budget share squares with a reasonable system of maintenance on roads it knows are in the worst condition.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Surrey County Council" 
        councilSlug="surrey" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/surrey"
        headline="Surrey Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Surrey</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Surrey: 273,383 Potholes in Five Years — and Rising
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            One of England's wealthiest counties spends roughly <strong className="text-emerald-400">triple its 
            DfT capital allocation</strong> and earns a GREEN spend scorecard. Yet Surrey's overall DfT rating 
            is <strong className="text-amber-400">AMBER</strong> — for both condition and best practice — 
            because two-thirds of its 4,844km network is surveyed only <strong className="text-rose-400">once 
            every four years</strong>, 22% of those unclassified roads are in RED condition, and the council's 
            own pothole count just hit a five-year record.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">59,924</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              That's roughly 164 potholes every single day — the highest annual figure in 
              Surrey's five years of published data, and the council says the trend is still rising.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Spending Paradox */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Triple the Allocation, Still AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of spending data from Surrey's own transparency report — the chequebook is not the problem
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Actual capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£33.5m</td>
                  <td className="px-4 py-3 text-center font-bold">£57.0m</td>
                  <td className="px-4 py-3 text-center">51%</td>
                  <td className="px-4 py-3 text-center">49%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£25.7m</td>
                  <td className="px-4 py-3 text-center font-bold">£52.0m</td>
                  <td className="px-4 py-3 text-center">50%</td>
                  <td className="px-4 py-3 text-center">50%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£25.7m</td>
                  <td className="px-4 py-3 text-center font-bold">£55.8m</td>
                  <td className="px-4 py-3 text-center">53%</td>
                  <td className="px-4 py-3 text-center">47%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£32.0m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£111.5m</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£28.6m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£100.5m</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr className="bg-surface-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£37.3m</td>
                  <td className="px-4 py-3 text-center font-bold">£86.2m</td>
                  <td className="px-4 py-3 text-center font-bold">61%</td>
                  <td className="px-4 py-3 text-center font-bold">39%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£377m Spent Against £145m Allocated</h3>
              <p className="text-surface-600 text-sm mb-3">
                Over the five completed years, Surrey spent around <strong>£377m of capital</strong> against 
                roughly <strong>£145m of DfT allocations</strong> — about 2.6 times the government funding. 
                In 2023/24 and 2024/25 the multiple was nearly 3.5 times.
              </p>
              <p className="text-surface-600 text-sm">
                That is what a GREEN spend scorecard looks like. It is also why the AMBER condition rating 
                matters more: money on this scale has not stopped pothole numbers rising to a record high.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 13% Figure</h3>
              <p className="text-amber-800 text-sm mb-3">
                Surrey's report states that "on average over the past 5 years approximately 13% of the spend 
                on reactive maintenance is used for filling potholes."
              </p>
              <p className="text-amber-800 text-sm">
                Read that alongside the reactive share of the budget — which ran at 43-50% in four of the 
                last six years. A council spending heavily on reaction rather than prevention is a council 
                whose network keeps generating defects faster than planned works remove them.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Why GREEN Spend Doesn't Defeat Your Claim</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£100.5m</div>
                <div className="text-brand-300 text-sm">Capital spend 2024/25 — 3.5× the DfT allocation</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">DfT Condition scorecard despite that spend</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">59,924</div>
                <div className="text-brand-300 text-sm">Potholes filled in the same year — a record</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Section 58 isn't a spending test. It asks whether the <span className="text-amber-400">specific 
              defect</span> that damaged your vehicle was reasonably inspected and repaired. Surrey's own 
              data shows a network producing defects at a rate even £100m a year hasn't tamed.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Surrey's own transparency report — classified roads steady, 
            unclassified roads heading the wrong way
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (598km — 12% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">4%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 7% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">24%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 28%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">71%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 66%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: Surrey's main roads have genuinely improved. But A-roads are barely 
              one-eighth of the network — and they are surveyed every two years, with half done each year.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,014km — 21% of network): modest gains, then flat</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">5%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold">69%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads improved to 2022, then plateaued. Just under a third of the B/C network 
              still needs — or will soon need — maintenance. Like A-roads, these are surveyed on a 
              two-year cycle, not annually.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (3,253km — 67% of network): worst figure on record</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">13% <span className="text-surface-400 text-xs">(Covid — ~¼ of network not surveyed)</span></td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">15% <span className="text-surface-400 text-xs">(new video/AI survey method introduced)</span></td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">21%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">22%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              22% RED means roughly 715km of Surrey's residential streets, estate roads and rural lanes 
              should currently be considered for maintenance — on the council's own figures, the worst 
              result in five years of published data.
            </p>
          </div>
        </div>
      </section>

      {/* The Four-Year Survey Gap */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 3,253km Four-Year Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            Two-thirds of Surrey's network is unclassified — and the council's own report says how rarely it's surveyed
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Unclassified roads are traditionally surveyed once every 4 years."
              source="Surrey County Council Local Highways Maintenance Transparency Report"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Four Years Between Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                A-roads are surveyed every two years. B and C roads, every two years. But the unclassified 
                network — <strong>3,253km, two-thirds of every road Surrey maintains</strong> — has 
                traditionally been condition-surveyed just once every four years.
              </p>
              <p className="text-surface-600 text-sm">
                Surrey says it expects to move U-roads to a two-year survey cycle "by the end of 2026". 
                That is an improvement — and an implicit acknowledgement that the four-year cycle it ran 
                for years was not frequent enough.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Methodology Asterisk — Read It Carefully</h3>
              <p className="text-amber-800 text-sm mb-3">
                Surrey switched U-road surveys to video and machine-learning technology in 2022 and says 
                the new method scores about 5 percentage points more of the network RED, so the 2023-24 
                rise is "primarily due to the change in survey methodology rather than a significant 
                deterioration in condition."
              </p>
              <p className="text-amber-800 text-sm">
                Take that at face value and the picture is still not improvement: RED rose from 16% (2020) 
                to 22% (2024) — a 6-point rise against a 5-point methodology adjustment. And the council's 
                2021 figure is itself flagged as artificially low, because Covid lockdowns left a quarter 
                of the network unsurveyed that year.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing and maintaining the condition of its roads. For Surrey's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it up to four years before your incident?</li>
              <li>• If 22% of U-roads are RED, what was the council's plan for yours?</li>
              <li>• How does the council track deterioration on a road it measures once every four years?</li>
              <li>• Can pre-2022 and post-2022 condition records even be compared after the survey technology changed?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of two-thirds of its network when its own report 
              says that network was traditionally surveyed once every four years.
            </p>
          </div>
        </div>
      </section>

      {/* The Pothole Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            273,383 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Surrey's own pothole counts — the highest annual figure came last, not first
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day (average)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">53,611</td>
                  <td className="px-6 py-4 text-center">~147</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">50,881</td>
                  <td className="px-6 py-4 text-center">~139</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">56,509</td>
                  <td className="px-6 py-4 text-center">~155</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">52,458</td>
                  <td className="px-6 py-4 text-center">~144</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">59,924</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~164</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">273,383</td>
                  <td className="px-6 py-4 text-center font-bold">~150</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The current trend in pothole numbers is showing an increase, however that is because we are being more proactive with our pothole repairs and have enabled our repair gangs to fix other potholes they identify in the vicinity of a pothole that has been reported by our inspectors or customers."
              source="Surrey County Council Local Highways Maintenance Transparency Report"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~150 Potholes a Day, Every Day, For Five Years</h3>
              <p className="text-surface-600 text-sm mb-3">
                Whatever the explanation for the rising trend, the underlying fact is that Surrey's network 
                produced over a quarter of a million potholes worth filling in five years. A network 
                generating defects at that rate is, by definition, one where potholes routinely form — 
                and persist — between inspections.
              </p>
              <p className="text-surface-600 text-sm">
                That is exactly the scenario where prior reports and dated photographs decide claims: 
                if the defect existed long enough to be found, the inspection system should have found it.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Patching Caveat</h3>
              <p className="text-amber-800 text-sm mb-3">
                Surrey's report also notes: "We are now repairing multiple potholes, or other defects in 
                the road that are close together, as one patch."
              </p>
              <p className="text-amber-800 text-sm">
                Where multiple potholes are treated as one larger patch, the published counts may understate 
                the number of individual defects on the road. The 273,383 figure is a floor, not a ceiling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Prioritisation Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2025/26 Prioritisation Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Where the money is going next — in the council's own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="We will focus on improving the most used roads, our A and B network, with a higher proportion of budget allocation compared to the lesser used roads on the C and D road network."
              source="Surrey County Council Local Highways Maintenance Transparency Report — Specific plans for 2025 to 2026"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Surrey is openly directing a higher share of its budget at A and B roads — the parts of 
                the network that are already improving — while the "lesser used" roads receive 
                proportionally less.
              </p>
              <p className="text-surface-600 text-sm">
                But the weakest condition data in the entire report belongs to the unclassified network: 
                22% RED and climbing on the published figures. The roads in the worst documented state 
                are the ones getting the smaller budget share.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your pothole on a C or unclassified road receiving a reduced budget share?</li>
                <li>• If the council knows 22% of U-roads are RED, what is the maintenance plan for yours?</li>
                <li>• Does a deprioritised budget square with a "reasonable" system of maintenance on the worst-condition roads?</li>
              </ul>
              <p className="text-amber-800 text-sm mt-3 italic">
                To be fair to Surrey: the same plan promises 92 miles of resurfacing in 2025/26 and 
                concrete-road repairs in residential areas. The question is whether your road made the list.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Claiming against Surrey */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Surrey spends real money and resurfaces real roads — here's how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — roughly 2.6× its DfT allocation over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 486 miles of carriageway resurfaced in the last five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving — RED down from 7% to 4%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management approach aligned to the Well-Managed Highway Infrastructure code
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Larger, more durable patch repairs replacing single pothole fills
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A and B roads. Generic, evidence-light claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition and AMBER best practice on the DfT scorecards — spend alone didn't earn a clean bill
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 67% of the network surveyed only once every four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 22% of U-roads RED in 2024 — the worst published figure in five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 273,383 potholes filled in five years, with the count rising to a record 59,924
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2025/26 budget openly weighted away from the "lesser used" roads in the worst condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Survey methodology changed mid-series — pre- and post-2022 U-road records aren't directly comparable
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend scorecard and a real resurfacing programme, your claim 
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on an unclassified road, the four-year survey cycle is your strongest structural argument</li>
              <li>• Whether your road sits in the "lesser used" category the 2025/26 budget deprioritises</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Surrey's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Surrey?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No budget-prioritisation analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 273,383 potholes in five years cited</li>
                <li>✅ Four-year U-road survey cycle argued</li>
                <li>✅ 22% RED unclassified network documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Surrey</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=surrey"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Surrey Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Surrey spends roughly three times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN — Surrey spent £100.5m of capital against a £28.6m 
                allocation in 2024/25 — but the ratings that matter for your claim are Condition and Best 
                Practice, and both are AMBER. Section 58 of the Highways Act 1980 turns on whether the 
                specific defect that damaged your vehicle was reasonably inspected and repaired, not on how 
                much the council spends in aggregate. A council filling nearly 60,000 potholes a year is, 
                by its own numbers, running a network that produces defects faster than its spending 
                prevents them.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Surrey?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 3,253km — 67% of Surrey's 4,844km network — and the council's 
                own report states they are "traditionally surveyed once every 4 years." In 2024, 22% of 
                U-roads were in RED condition, the worst figure in the five years published. A four-year 
                gap between condition surveys on two-thirds of the network is a structural weakness in any 
                Section 58 defence: the council must show it had reasonable knowledge of the road's 
                condition, and a road last surveyed up to four years ago is hard to know much about.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Surrey says the rise in RED unclassified roads is mostly a survey methodology change — does that hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not really. Surrey switched to a video and machine-learning survey in 2022 and says the new 
                technology adds about 5 percentage points to the RED score, so "the increase in red condition 
                shown in 2023 and 2024 is primarily due to the change in survey methodology rather than a 
                significant deterioration in condition." But RED went from 16% in 2020 to 22% in 2024 — a 
                6-point rise — so even on the council's own adjustment the network has not improved. And 
                whatever the cause of the measurement, 22% RED means roughly 715km of Surrey's residential 
                roads should currently be considered for maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs hit a record 59,924 in 2024/25 — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It means Surrey's network produces potholes at an extraordinary rate — about 164 filled 
                every day in 2024/25, and 273,383 over five years. The council frames the rising trend as 
                proactivity: repair gangs now "fix other potholes they identify in the vicinity of a pothole 
                that has been reported." For a claimant, the volume cuts the other way: a network generating 
                this many defects is one where potholes routinely form, and persist, between inspections — 
                exactly the scenario where prior reports and dated photographs decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Surrey is prioritising A and B roads in 2025/26 — does that matter for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. The council's own plan says it will "focus on improving the most used roads, our A 
                and B network, with a higher proportion of budget allocation compared to the lesser used 
                roads." A and B roads together are about a third of the network; the C and unclassified 
                roads receiving a smaller share are where condition is weakest — 22% of U-roads are RED. 
                If your pothole was on a deprioritised road, the question for the council is how a reduced 
                budget share squares with a reasonable system of maintenance on roads it knows are in the 
                worst condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.surreycc.gov.uk/roads-and-transport/roadworks-and-maintenance/roadworks/department-for-transport-capital-funding/transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Surrey County Council Local Highways Maintenance Transparency Report
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
